import { hasBackofficeAccess } from "./roles";
import { parseUrlParams } from "./urlParser";
import { recordApiFailure } from "./apiErrors";

let currentUrlFetched = {};

/* Generic Helpers */

export function jsonContentType(headers) {
  return {
    ...headers,
    "Content-Type": "application/json",
  };
}

function handleResponse(response) {
  if (!response.ok) {
    recordApiFailure({
      url: response.url,
      status: response.status,
      statusText: response.statusText,
    });
    if (response.headers.get("content-type").includes("application/json"))
      return response.json().then((errors) => {
        const errorMessage = errors.errors
          ? errors.errors.join(". ")
          : errors.message || errors.error;
        return Promise.reject(errorMessage);
      });

    return Promise.reject(
      new Error(`${response.status} ${response.statusText}`)
    );
  }

  if (response.status === 204)
    // no content, early return
    return Promise.resolve(response.status);

  return response.json();
}

// devise_token_auth reports validation failures as { full_messages: [...] } but
// 401/422 as a bare array, so accept both shapes.
export function authErrorMessages(errors) {
  if (Array.isArray(errors)) return errors;
  return errors?.full_messages || ["Something went wrong, please try again."];
}

function handleResponseAndErrors(response) {
  return response
    .json()
    .then((json) => json)
    .catch(() => response);
}

//request next data in case of multipages answer from DynamoDB
function handleNext(
  user,
  url_api,
  data,
  property_name,
  _record = null,
  calledBy
) {
  if (calledBy != null)
    if (url_api !== currentUrlFetched[calledBy])
      return Promise.reject(new Error(`All next has been cancelled`));

  if (_record == null) _record = data[property_name];
  if (data.meta.next_page) {
    var sep = url_api.includes("?") ? "&" : "?";
    return fetch(
      `${url_api}${sep}next_page=${encodeURIComponent(data.meta.next_page)}`,
      {
        headers: user.identity,
      }
    )
      .then(handleResponse)
      .then((data) =>
        handleNext(
          user,
          url_api,
          data,
          property_name,
          _record.concat(data[property_name]),
          calledBy
        )
      )
      .catch((response) =>
        Promise.reject(new Error(`${response.status} ${response.statusText}`))
      );
  }
  return _record;
}

/* General purpose */
export function sendMail(mailContent, user) {
  return fetch(`/api/client/contact`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(mailContent),
  }).then(handleResponse);
}

export function sendErrorReport(mailContent, user) {
  return fetch(`/api/client/error_report`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(mailContent),
  }).then(handleResponse);
}

/* Current User and Users */
export function requestFetchUser(userId, user) {
  return fetch(`/api/users/${userId}`, {
    headers: user.identity,
  }).then(handleResponse);
}

// The page-based UI is translated to the backend's offset/limit contract here, and the
// { rows, count } response is mapped back to { users, total } so the rest of the app is unchanged.
export function requestFetchUsers(
  user,
  { page = 1, perPage = 25, q = "", filters = {}, sort = "" } = {}
) {
  const params = new URLSearchParams({
    offset: (page - 1) * perPage,
    limit: perPage,
  });
  if (q) params.set("q", q);
  if (sort) params.set("sort", sort);
  if (filters.company_id) params.set("company_id", filters.company_id);
  if (filters.access) params.set("access", filters.access);
  return fetch(`/api/users?${params.toString()}`, {
    headers: user.identity,
  })
    .then(handleResponse)
    .then(({ rows, count }) => ({ users: rows, total: count }));
}

export function requestUpdateUser(userId, attrs, user) {
  if (userId)
    return fetch(`/api/users/${userId}`, {
      method: "PATCH",
      headers: jsonContentType(user.identity),
      body: JSON.stringify(attrs),
    }).then(handleResponse);
  else
    return fetch(`/api/users/add`, {
      method: "POST",
      headers: jsonContentType(user.identity),
      body: JSON.stringify(attrs),
    }).then(handleResponse);
}

export function requestUpdateUserRole(userId, attrs, user) {
  return fetch(`/api/users/${userId}/role`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(attrs),
  }).then(handleResponse);
}

export function requestPasswordResetInstructions(email) {
  return fetch("/api/users/password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      redirect_url: "/password/edit",
    }),
  }).then(handleResponseAndErrors);
}

export function requestUpdatePassword(
  password,
  passwordConfirmation,
  urlParams
) {
  const params = parseUrlParams(urlParams);
  const { token, client, uid } = params;
  const headers = {
    token,
    client,
    uid,
  };

  return fetch("/api/users/password", {
    method: "PUT",
    headers: jsonContentType({
      "access-token": headers.token,
      client: headers.client,
      uid: headers.uid,
    }),
    body: JSON.stringify({
      password,
      password_confirmation: passwordConfirmation,
    }),
  }).then(handleResponseAndErrors);
}

export function requestDeleteUser(userToDeleteId, user) {
  return fetch(`/api/users/${userToDeleteId}`, {
    method: "DELETE",
    headers: user.identity,
  }).then(handleResponse);
}

export function requestFetchUserContractsInfos(user) {
  return fetch(`/api/users/${user.id}/contract_infos`, {
    headers: user.identity,
  }).then(handleResponse);
}

// Light list of every campaign across companies, with its company and site ids.
export function requestFetchAllContractInfos(user) {
  return fetch("/api/contract_infos?with_site_ids=true", {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestFetchUserContracts(user) {
  return fetch("/api/client/contract_infos", {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestGlobalSearch(user, query) {
  return fetch(`/api/client/search?q=${encodeURIComponent(query)}`, {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestFetchUserTrials(user) {
  var url_api = "/api/client/trials";
  return fetch(url_api, {
    headers: user.identity,
  })
    .then(handleResponse)
    .then((data) => handleNext(user, url_api, data, "trials"));
}

/* Treatment Chains */

export function requestFetchTreatmentChains(user) {
  return fetch("/api/treatment_chains", {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestFetchTreatmentChain(treatmentChainId, user) {
  return fetch(`/api/treatment_chains/${treatmentChainId}`, {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestDuplicateChain(treatmentChainId, user) {
  return fetch("/api/treatment_chains", {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({ original_chain_id: treatmentChainId }),
  }).then(handleResponse);
}

export function requestUpdateTreatmentChain(treatmentChain, payload, user) {
  return fetch(`/api/treatment_chains/${treatmentChain.id}`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(payload),
  }).then(handleResponse);
}

/* Treatments */

export function requestFetchAbortedTreatments(user) {
  return fetch("/api/treatments/aborted", {
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestFetchTreatment(treatmentId, user) {
  return fetch(`/api/treatments/${treatmentId}`, {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestCreateTreatment(serviceId, treatmentPayload, user) {
  return fetch(`/api/provision_services/${serviceId}/treatments`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(treatmentPayload),
  }).then(handleResponse);
}

export function requestFileUploadURL(site, user) {
  return fetch(`/api/client/sites/${site.id}/experiments/attachment`, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestStartTreatment(treatment, treatmentPayload, user) {
  const url = `/api/treatments/${treatment.id}/start`;
  return fetch(url, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(treatmentPayload),
  }).then(handleResponse);
}

export function requestRecoverTreatment(treatment, user) {
  const url = `/api/treatments/${treatment.id}/recover`;
  return fetch(url, {
    method: "PATCH",
    headers: user.identity,
  }).then(handleResponse);
}

export function requestDeleteTreatment(treatmentId, user) {
  return fetch(`/api/treatments/${treatmentId}`, {
    method: "DELETE",
    headers: user.identity,
  }).then(handleResponse);
}

export function requestFetchAllTreatments(user) {
  return fetch("/api/treatments", {
    headers: user.identity,
  }).then(handleResponse);
}

/* Contracts and Systems */

export function requestFetchContracts(user) {
  return fetch("/api/contracts", {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestCreateContract(newContract, user) {
  return fetch("/api/contracts", {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(newContract),
  }).then(handleResponse);
}

export function requestUpdateContract(contract, user) {
  return fetch(`/api/contracts/${contract.id}`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(contract),
  }).then(handleResponse);
}

export function requestDeleteContract(contractId, user) {
  return fetch(`/api/contracts/${contractId}`, {
    method: "DELETE",
    headers: user.identity,
  }).then(handleResponse);
}

export function requestFetchSystems(user) {
  return fetch("/api/systems", {
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

/* Service Provisions */

export function requestFetchProvisions(contractId, user) {
  return fetch(`/api/contracts/${contractId}/provisions`, {
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestFetchPendingProvisions(user) {
  return fetch("/api/provisions/pending", {
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestFetchProvision(provisionId, user) {
  return fetch(`/api/provisions/${provisionId}`, {
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestFetchProvisionTreatments(provisionId, user) {
  return fetch(`/api/provisions/${provisionId}/treatments`, {
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestFetchProvisionService(serviceId, user) {
  return fetch(`/api/provision_services/${serviceId}`, {
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestCreateProvision(contractId, provisionPayload, user) {
  return fetch(`/api/contracts/${contractId}/provisions`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(provisionPayload),
  }).then(handleResponse);
}

export function requestUpdateProvision(provisionId, provisionPayload, user) {
  return fetch(`/api/provisions/${provisionId}`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(provisionPayload),
  }).then(handleResponse);
}

export function requestDeleteProvision(provisionId, user) {
  return fetch(`/api/provisions/${provisionId}`, {
    method: "DELETE",
    headers: user.identity,
  }).then(handleResponse);
}

/* Consumptions */

export function requestFetchConsumption(month, user) {
  return fetch(`/api/consumption?month=${month}`, {
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

/* Client */

export function requestFetchClientProvisions(user) {
  return fetch("/api/client/provisions", {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestFetchClientProvision(provisionId, user) {
  return fetch(`/api/client/provisions/${provisionId}`, {
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestFetchClientProvisionTreatments(provisionId, user) {
  return fetch(`/api/client/provisions/${provisionId}/treatments`, {
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestFetchAllClientTreatments(user) {
  return fetch("/api/client/treatments", {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestFetchClientSites(contract, user) {
  return fetch(`/api/client/contract_infos/${contract.id}/sites`, {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestFetchClientSite(site, user) {
  return fetch(`/api/client/sites/${site.id}`, {
    headers: user.identity,
  }).then(handleResponse);
}

// Upload-session status counts grouped by campaign for the whole portfolio.
export function requestUploadSummaryByCampaign(user) {
  return fetch(`/api/client/upload_sessions_summary`, {
    headers: user.identity,
  }).then(handleResponse);
}

// Upload-session status counts grouped by site for a single campaign.
export function requestUploadSummaryBySite(campaignId, user) {
  return fetch(
    `/api/client/contract_infos/${campaignId}/upload_sessions_summary`,
    { headers: user.identity }
  ).then(handleResponse);
}

export function requestFetchTraitGroups(user) {
  return fetch("/api/client/trait_groups", {
    headers: user.identity,
  }).then(handleResponse);
}

/* Company */

export function requestFetchCompanyContracts(user, company) {
  var url_api = `/api/companies/${company.id}/contract_infos`;
  return fetch(url_api, {
    headers: user.identity,
  }).then(handleResponse);
}

/* Result Map */

export function requestFetchResultMapFeatures(user, site, trial_date) {
  var url_api = `/api/client/trials/${site.id}/features?date=${trial_date}`;
  return fetch(url_api, {
    headers: user.identity,
  })
    .then(handleResponse)
    .then((data) => handleNext(user, url_api, data, "features"));
}

export function requestFeaturesPropertiesByDate(user, site) {
  var url_api = `/api/client/trials/${site.id}/features?date=all`;
  if (currentUrlFetched.requestFeaturesPropertiesByDate !== url_api) {
    currentUrlFetched.requestFeaturesPropertiesByDate = url_api;
    // Only a request still running is "in progress": kept after it ends, the
    // url rejected the next visit to the same site. Not cleared when another
    // site's request has replaced it.
    return fetch(url_api, {
      headers: user.identity,
    })
      .then(handleResponse)
      .then((data) =>
        handleNext(
          user,
          url_api,
          data,
          "features",
          null,
          "requestFeaturesPropertiesByDate"
        )
      )
      .finally(() => {
        if (currentUrlFetched.requestFeaturesPropertiesByDate === url_api)
          currentUrlFetched.requestFeaturesPropertiesByDate = null;
      });
  } else {
    return Promise.reject(new Error("fetch already in progress"));
  }
}

/* Administration */

/* Administration - Companies */

export function requestFetchCompanies(user) {
  return fetch("/api/companies", {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestUpdateCompany(company, user) {
  return fetch(`/api/companies/${company.id}`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(company),
  }).then(handleResponse);
}

export function requestCreateCompany(company, user) {
  return fetch(`/api/companies`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({ name: company.name }),
  }).then(handleResponse);
}

/* Administration Contracts */

export function requestFetchContractInfo(contract, user) {
  return fetch(`/api/contract_infos/${contract.id}`, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestUpdateContractInfo(contract, user) {
  return fetch(`/api/contract_infos/${contract.id}`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(contract),
  }).then(handleResponse);
}

export function requestCreateContractInfo(company, contract, user) {
  return fetch(`/api/companies/${company.id}/contract_infos`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(contract),
  }).then(handleResponse);
}

export function requestDeleteContractInfo(contract, user) {
  return fetch(`/api/contract_infos/${contract.id}`, {
    method: "DELETE",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function fetchUploadSessions(site, user) {
  return fetch(`/api/sites/${site.id}/upload_sessions`, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function fetchUploadSession(us, user, includeStatistics) {
  const params = includeStatistics ? `?include_statistics=true` : "";
  const url = hasBackofficeAccess(user.self)
    ? `/api/upload_sessions/${us.id}${params}`
    : `/api/client/upload_sessions/${us.id}${params}`;
  return fetch(url, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function fetchUploadFiles(uploadSessionId, category, user) {
  // Downloading files is a backoffice-only feature; request download_url=true so
  // the backend includes the presigned URLs (omitted by default).
  const url =
    `/api/upload_sessions/${uploadSessionId}/upload_files` +
    `?category=${encodeURIComponent(category)}&download_url=true`;
  return fetch(url, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function deleteUploadSession(us, user) {
  return fetch(`/api/upload_sessions/${us.id}`, {
    method: "DELETE",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function updateUploadSession(us, user) {
  return fetch(`/api/upload_sessions/${us.id}`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(us),
  }).then(handleResponse);
}

export function sendAdvancedLiteral(us, user) {
  return fetch(`/api/upload_sessions/${us.id}/advanced_literal`, {
    method: "POST",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function fetchAllUploadSessions(user, status = null, system = null) {
  let url = "/api/upload_sessions" + (status ? "?status=" + status : "");
  let sep = url.includes("?") ? "&" : "?";
  url += system ? sep + "system=" + system : "";

  return fetch(url, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function fetchAllBulkExports(user) {
  return fetch("/api/bulk_exports", {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

/* Administration Sites */

export function requestFetchSites(contract, user) {
  return fetch(`/api/contract_infos/${contract.id}/sites`, {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestUpdateSite(site, user) {
  return fetch(`/api/sites/${site.id}`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(site),
  }).then(handleResponse);
}

//Update site from client endpoint is limited to experiment status
export function requestUpdateClientSite(site, user) {
  return fetch(`/api/client/sites/${site.id}`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({ experiment_status: site.experiment_status }),
  }).then(handleResponse);
}

export function requestUpdateClientSiteExperimentStatus(
  site,
  user,
  missionDate
) {
  return fetch(`/api/client/sites/${site.id}/experiment_status`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({
      experiment_status: site.experiment_status,
      acquisition_date: missionDate,
    }),
  }).then(handleResponse);
}

export function requestFetchSite(site, user) {
  return fetch(`/api/sites/${site.id}`, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestCreateSite(contract, site, user) {
  return fetch(`/api/contract_infos/${contract.id}/sites`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(site),
  }).then(handleResponse);
}

export function requestClientCreateSite(contract, site, user) {
  return fetch(`/api/client/contract_infos/${contract.id}/sites`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(site),
  }).then(handleResponse);
}

export function requestClientUpdateSite(site, user) {
  return fetch(`/api/client/sites/${site.id}`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(site),
  }).then(handleResponse);
}

// Triggers an asynchronous multi-target bulk export (ZIP).
// payload: { targets: [{ contract_info_id, site_ids: [] }], dates?, formats?,
// files?, filter? }. An empty selection means every accessible campaign.
// Returns 202 + the pending BulkExport; the user is notified by email when it
// is ready.
export function requestClientBulkExport(payload, user) {
  return fetch("/api/client/bulk_exports", {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(payload),
  }).then(handleResponse);
}

// Downloads one site's features on one date, right away (no email, no queue):
// the bulk export drawer takes this path when the selection is a single date.
// Resolves { blob, filename }; rejects with the HTTP status on failure.
export function requestDownloadSiteFeatures(siteId, date, format, user) {
  const url = `/api/download/${siteId}/features.${format}?date=${encodeURIComponent(
    date
  )}`;
  return fetch(url, { headers: user.identity }).then((response) => {
    if (!response.ok) return Promise.reject(response.status);
    const disposition = response.headers.get("content-disposition") || "";
    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
    const filename = match?.[1]
      ? match[1].replace(/['"]/g, "")
      : `${date}.${format}`;
    return response.blob().then((blob) => ({ blob, filename }));
  });
}

export function requestDeleteSite(site, user) {
  return fetch(`/api/sites/${site.id}`, {
    method: "DELETE",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestFetchTrialBySite(site, user) {
  return fetch(`/api/trials/${site.id}`, {
    headers: user.identity,
  }).then(handleResponse);
}

/* Mission */
export function requestCreateMission(site, mission, user) {
  return fetch(`/api/sites/${site.id}/missions`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(mission),
  }).then(handleResponse);
}

export function requestDeleteMission(mission, user) {
  return fetch(`/api/missions/${mission.id}`, {
    method: "DELETE",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

/* Crop */

export function requestFetchCrops(user) {
  return fetch(`/api/crops`, {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestCreateCrop(crop, user) {
  return fetch(`/api/crops`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(crop),
  }).then(handleResponse);
}

export function requestDeleteCrop(crop, user) {
  return fetch(`/api/crops/${crop.id}`, {
    method: "DELETE",
    headers: user.identity,
  }).then(handleResponse);
}

export function requestUpdateCrop(crop, user) {
  return fetch(`/api/crops/${crop.id}`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(crop),
  }).then(handleResponse);
}

// Experiments

export const requestFetchSiteExperiments = (site, user) => {
  return fetch(`/api/sites/${site.id}/experiments`, {
    headers: user.identity,
  }).then(handleResponse);
};

export const requestFetchClientExperiments = (site, user) => {
  return fetch(`/api/client/sites/${site.id}/experiments`, {
    headers: user.identity,
  }).then(handleResponse);
};

export const requestFetchClientSiteExperiments = (site, user) => {
  return fetch(`/api/client/sites/${site.id}/experiments`, {
    headers: user.identity,
  }).then(handleResponse);
};

export function requestCreateClientExperiment(site, user, experiment) {
  return fetch(`/api/client/sites/${site.id}/experiments`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(experiment),
  }).then(handleResponse);
}

export function requestUpdateClientExperiment(experiment, user) {
  return fetch(`/api/client/experiments/${experiment.id}`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(experiment),
  }).then(handleResponse);
}

export function requestDeleteClientExperiment(experimentId, user) {
  return fetch(`/api/client/experiments/${experimentId}`, {
    method: "DELETE",
    headers: user.identity,
  }).then(handleResponse);
}

export function requestUpdateContractUsers(contractUserList, contractId, user) {
  return fetch(`/api/contract_infos/${contractId}/users`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(contractUserList),
  }).then(handleResponse);
}

export function requestUpdateContractUser(contractId, user) {
  return fetch(`/api/contract_infos/${contractId}/user`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestUpdateUserContracts(userContractList, userId, user) {
  return fetch(`/api/users/${userId}/contract_infos`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(userContractList),
  }).then(handleResponse);
}

/* Applications */

export function requestCreateApplication(companyId, application, user) {
  return fetch(`/api/companies/${companyId}/applications`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(application),
  }).then(handleResponse);
}

export function requestUpdateApplication(applicationId, application, user) {
  return fetch(`/api/applications/${applicationId}`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(application),
  }).then(handleResponse);
}

export function requestDeleteApplication(applicationId, user) {
  return fetch(`/api/applications/${applicationId}`, {
    method: "DELETE",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

// Replaces the whole application list of a campaign: callers must send the
// applications to keep along with the ones they add.
export function requestUpdateContractApplications(
  contractId,
  applicationList,
  user
) {
  return fetch(`/api/contract_infos/${contractId}/applications`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(applicationList),
  }).then(handleResponse);
}

/* Mission Monitorings */
export function requestFetchMissionsMonitoring(contractUuids, user) {
  let params = "";
  contractUuids.forEach((id) => {
    params += `&contract_infos[]=${id}`;
  });

  return fetch(`/api/client/missions_monitoring?${params}`, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestFetchMissionMonitoring(missionMonitoring, user) {
  return fetch(`/api/client/missions_monitoring/${missionMonitoring.id}`, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestDeleteMissionMonitoring(missionMonitoring, user) {
  return fetch(`/api/missions_monitoring/${missionMonitoring.id}`, {
    method: "DELETE",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

// Comments
export function requestFetchMissionsMonitoringComments(
  missionMonitoring,
  user
) {
  return fetch(`/api/missions_monitoring/${missionMonitoring.id}/comments`, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestCreateMissionsMonitoringComment(
  missionMonitoring,
  user,
  comment
) {
  return fetch(
    `/api${
      !hasBackofficeAccess(user.self) ? "/client" : ""
    }/missions_monitoring/${missionMonitoring.id}/comments`,
    {
      method: "POST",
      headers: jsonContentType(user.identity),
      body: JSON.stringify(comment),
    }
  ).then(handleResponse);
}

export function requestMarkAsReadMissionMonitoringComment(
  user,
  comment,
  recipient,
  date
) {
  let url = `/api/client/mission_monitoring/comments/${comment.id}/mark_as_read`;
  let body = { reading_date: date };

  if (hasBackofficeAccess(user.self)) {
    url = `/api/mission_monitoring/comments/${comment.id}/mark_as_read`;
    body = { recipient_id: recipient.id, reading_date: date };
  }

  return fetch(url, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(body),
  }).then(handleResponse);
}

// Flags & steps
export function requestFetchMissionsMonitoringFlags(missionMonitoring, user) {
  return fetch(`/api/missions_monitoring/${missionMonitoring.id}/flags`, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestCreateMissionsMonitoringFlag(
  missionMonitoring,
  user,
  flag
) {
  return fetch(`/api/missions_monitoring/${missionMonitoring.id}/flags`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(flag),
  }).then(handleResponse);
}

export function requestUpdateMissionsMonitoringFlag(flag, user) {
  return fetch(`/api/flags/${flag.id}`, {
    method: "PATCH",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(flag),
  }).then(handleResponse);
}

export function requestDeleteMissionsMonitoringFlag(flag, user) {
  return fetch(`/api/flags/${flag.id}`, {
    method: "DELETE",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestFetchMissionsMonitoringSteps(missionMonitoring, user) {
  return fetch(`/api/missions_monitoring/${missionMonitoring.id}/steps`, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestFeaturePresignedUrl(site, plot, date, user) {
  return fetch(
    `/api/client/trials/${site.id}/features_sample/${plot.id}?date=${date}`,
    {
      method: "GET",
      headers: jsonContentType(user.identity),
    }
  ) // TODO
    .then(handleResponse)
    .then((res) => {
      return { url: res.url, plotId: plot.id };
    });
}

// Plot Rating
export function requestCreateOrUpdatePlotRating(
  site,
  feature,
  rating,
  date,
  user
) {
  return fetch(`/api/client/sites/${site.id}/plot_ratings`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({
      acquisition_date: date,
      rating: rating,
      plot_id: feature.id,
    }),
  });
}

export function requestFetchPlotRatings(user, site, trial_date) {
  var url_api = `/api/client/sites/${site.id}/plot_ratings?date=${trial_date}`;
  return fetch(url_api, {
    headers: user.identity,
  }).then(handleResponse);
}

// Filtering profiles
export function requestFetchFilteringProfile(user, id) {
  return fetch(`/api/client/filtering_profiles/${id}`, {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestFetchFilteringProfiles(user, site) {
  return fetch(`/api/client/sites/${site.id}/filtering_profiles`, {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestCreateFilteringProfile(user, site, profile) {
  return fetch(`/api/client/sites/${site.id}/filtering_profiles`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(profile),
  }).then(handleResponse);
}

export function requestUpdateFilteringProfile(user, profile) {
  return fetch(`/api/client/filtering_profiles/${profile.id}`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({
      ...profile,
      filters: profile.filters.map(({ date, traits, excludedGroups }) => ({
        date,
        traits,
        excluded_groups: excludedGroups,
      })),
    }),
  }).then(handleResponse);
}

export function requestDeleteFilteringProfile(user, profile) {
  return fetch(`/api/client/filtering_profiles/${profile.id}`, {
    method: "DELETE",
    headers: jsonContentType(user.identity),
    body: JSON.stringify(profile),
  }).then(handleResponse);
}

export function requestSetCompletedTour(user) {
  return fetch(`/api/client/set_completed_tour`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

// Acquisition report
export function fetchAcquisitionReports(user, siteId) {
  var url_api = `/api/client/sites/${siteId}/hcc_missions`;
  return fetch(url_api, {
    headers: user.identity,
  }).then(handleResponse);
}

export function fetchHccMission(user, missionId) {
  var url_api = `/api/client/hcc_missions/${missionId}`;
  return fetch(url_api, {
    method: "GET",
    headers: jsonContentType(user.identity),
  }).then(handleResponse);
}

export function requestUpdatePlotRatingConfig(
  user,
  contract,
  plotRatingConfig
) {
  var url_api = `/api${
    hasBackofficeAccess(user.self) ? "" : "/client"
  }/contract_infos/${contract.id}`;
  return fetch(url_api, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({ plot_rating_config: plotRatingConfig }),
  }).then(handleResponse);
}

export function requestCreateClientRequest(user, siteId, requestType) {
  var url_api = `/api/client/sites/${siteId}/client_requests`;
  return fetch(url_api, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({ request_type: requestType }),
  }).then(handleResponse);
}

export function requestFetchScoutingFeatures(user, siteId, startDate, endDate) {
  var url_api = `/api/client/sites/${siteId}/scouting_features?startDate=${startDate}&endDate=${endDate}`;
  return fetch(url_api, {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestFetchScoutingDates(user, siteId) {
  var url_api = `/api/client/sites/${siteId}/scouting_dates`;
  return fetch(url_api, {
    headers: user.identity,
  }).then(handleResponse);
}

export function requestAnova(
  user,
  linearModel,
  data,
  experiment,
  properties,
  date
) {
  return fetch(`/api/client/anova`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({
      linear_model: linearModel,
      data,
      experiment,
      properties,
      date,
    }),
  }).then(handleResponse);
}

//Update sites from worldmap vue
export function requestUpdateSitePlotmapStatus(body, user) {
  return fetch(`/api/client/sites/${body.id}/plotmap_status`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({
      plotmap_status: body.plotmap_status,
      message: body.message,
      acquisition_date: body.acquisition_date,
    }),
  }).then(handleResponse);
}

// Same `site` shape as requestUpdateSite, so callers can swap one for the other.
// Client scope: open to anyone with access to the site, no create_site role.
export function requestUpdateSitePreferences(site, user) {
  return fetch(`/api/client/sites/${site.id}/preferences`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({ preferences: site.preferences }),
  }).then(handleResponse);
}

// Company-wide colour palette, inherited by every site of the contract.
// Client scope: open to anyone with access to the contract, no dedicated role.
// Backoffice and admin have no contract_infos_users row, so they go through
// requestUpdateContractInfo instead. Same `contract` shape for both.
export function requestUpdateContractPreferences(contract, user) {
  return fetch(`/api/client/contract_infos/${contract.id}/preferences`, {
    method: "PUT",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({ preferences: contract.preferences }),
  }).then(handleResponse);
}

export function requestTukey(
  user,
  linearModel,
  data,
  experiment,
  properties,
  date
) {
  return fetch(`/api/client/tukey`, {
    method: "POST",
    headers: jsonContentType(user.identity),
    body: JSON.stringify({
      linear_model: linearModel,
      data,
      experiment,
      properties,
      date,
    }),
  }).then(handleResponse);
}
